import React, { useState } from 'react';
import { 
  IoDocumentTextOutline, 
  IoCalendarOutline, 
  IoDownloadOutline, 
  IoPencilOutline, 
  IoTrashOutline,
  IoImageOutline,
  IoDocumentOutline,
  IoGridOutline
} from 'react-icons/io5';
import { LinkedContent } from './LinkedContentPreview';
import { formatDateOnly } from '../utils/dateUtils';
import { formatFileSize } from '../utils/fileUtils';
import { useDarkMode } from '../context/DarkModeContext';

export interface WorkExperienceDocument extends LinkedContent {
  type: 'work_experience_document';
  id: string;
  file_path: string;
  work_experience_id: string;
  preview: {
    title: string;       // Display label
    filename: string;    // Original filename
    date: string;       // Upload date
    mime_type: string;
    size: number;
    source: 'local' | 'google_drive';
  };
}

interface WorkExperienceDocumentPreviewProps {
  content: WorkExperienceDocument;
  isLoading?: boolean;
  onView?: () => void;
  onDownload?: () => void;
  onEditLabel?: (newLabel: string) => void;
  onDelete?: () => void;
  className?: string;
}

const VIEWABLE_MIME_TYPES = [
  'text/plain',
  'text/html',
  'text/css',
  'text/javascript',
  'application/json',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/svg+xml',
  'application/pdf'
];

export const WorkExperienceDocumentPreview: React.FC<WorkExperienceDocumentPreviewProps> = ({
  content,
  isLoading = false,
  onView,
  onDownload,
  onEditLabel,
  onDelete,
  className = '',
}) => {
  const { darkMode } = useDarkMode();
  
  const isViewable = VIEWABLE_MIME_TYPES.includes(content.preview.mime_type);

  const handleView = () => {
    if (!isViewable) {
      // If file is not viewable, trigger download instead
      onDownload?.();
      return;
    }
    onView?.();
  };

  const handleEditLabel = () => {
    if (!onEditLabel) return;
    
    const newLabel = window.prompt('Enter new label:', content.preview.title);
    if (newLabel !== null && newLabel.trim() !== '') {
      onEditLabel(newLabel.trim());
    }
  };

  const getFileIcon = () => {
    const mimeType = content.preview.mime_type;
    if (mimeType.startsWith('image/')) {
      return <IoImageOutline className="w-5 h-5" />;
    } else if (mimeType === 'application/pdf') {
      return <IoDocumentTextOutline className="w-5 h-5" />;
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return <IoDocumentOutline className="w-5 h-5" />;
    } else {
      return <IoGridOutline className="w-5 h-5" />;
    }
  };

  if (isLoading) {
    return (
      <div 
        className={`
          animate-pulse rounded-lg p-4
          ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}
          ${className}
        `}
      >
        <div className="h-4 bg-gray-400 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-400 rounded w-1/2 mb-2"></div>
        <div className="h-3 bg-gray-400 rounded w-2/3"></div>
      </div>
    );
  }

  // Ensure required preview data exists
  if (!content.preview?.title || !content.preview?.filename) {
    return null;
  }

  const ActionButton: React.FC<{
    onClick?: () => void;
    icon: React.ReactNode;
    label: string;
  }> = ({ onClick, icon, label }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    
    return (
      <div 
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <button
          onClick={onClick}
          className={`
            p-1 rounded-full
            hover:bg-gray-300 dark:hover:bg-gray-700
            text-gray-500 hover:text-gray-700 
            dark:text-gray-400 dark:hover:text-gray-200
            transition-colors duration-200
          `}
          aria-label={label}
        >
          {icon}
        </button>
        {showTooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded shadow-lg whitespace-nowrap z-10">
            {label}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900" />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className={`
        rounded-lg p-4 relative group
        ${darkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-100 hover:bg-gray-200'}
        transition-colors duration-200
        ${className}
      `}
    >
      <div className="flex items-center gap-2 mb-1">
        {getFileIcon()}
        <h3 className="font-medium">{content.preview.title}</h3>
      </div>
      
      <div className="text-sm text-gray-600 dark:text-gray-400">
        <div className="mb-1">
          {content.preview.filename}
          <span className="mx-2">•</span>
          {formatFileSize(content.preview.size)}
          {content.preview.source === 'google_drive' && (
            <>
              <span className="mx-2">•</span>
              <span className="text-blue-500">Google Drive</span>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <IoCalendarOutline />
          <span>{formatDateOnly(content.preview.date)}</span>
        </div>
      </div>

      {/* Quick actions menu */}
      <div className="absolute top-4 right-4 flex items-center gap-1">
        {isViewable && (
          <ActionButton
            onClick={handleView}
            icon={<IoDocumentTextOutline className="w-4 h-4" />}
            label="View"
          />
        )}
        <ActionButton
          onClick={onDownload}
          icon={<IoDownloadOutline className="w-4 h-4" />}
          label="Download"
        />
        {onEditLabel && (
          <ActionButton
            onClick={handleEditLabel}
            icon={<IoPencilOutline className="w-4 h-4" />}
            label="Edit Label"
          />
        )}
        {onDelete && (
          <ActionButton
            onClick={onDelete}
            icon={<IoTrashOutline className="w-4 h-4" />}
            label="Delete"
          />
        )}
      </div>
    </div>
  );
};

export default WorkExperienceDocumentPreview; 